import axios, { type AxiosError, type AxiosResponse } from 'axios';
import axiosRetry, { exponentialDelay, isSafeRequestError } from 'axios-retry';
import { parseISO } from 'date-fns';
import locks from 'locks';
import qs from 'qs';

import getRandomHex from '@iconik-io/core/src/get_random_hex';
import type { HttpHeaders } from '@iconik-io/core/src/http/generic_http_transport';
import sleep from '@iconik-io/core/src/sleep';
import {
  type EndpointRequest,
  isEndpointRequest,
} from '@iconik-io/iconik-api/src/client/request';
import {
  EndpointError,
  type EndpointResponse,
} from '@iconik-io/iconik-api/src/client/response';
import {
  AssetsHttpDeletePortalsByPortalId,
  AssetsHttpPostAssets,
} from '@iconik-io/iconik-api/src/services/assets';
import type { AuthTokenSchema } from '@iconik-io/iconik-api/src/services/auth';

import type {
  AppMiddleware,
  AppMiddlewareAPI,
  BaseAppState,
  EndpointRequestWeb,
} from '../../types';
import {
  encodeI64SentinelRequest,
  parseI64SentinelResponse,
} from './i64_sentinel';
import { throttle } from './throttle';

const API_LOGIN_URL = '/API/auth/v1/auth/simple/login/';

const mutex = locks.createMutex();

function lock(): Promise<void> {
  return new Promise((resolve, reject) => {
    mutex.timedLock(30000, (error) => {
      if (error) {
        reject(Error('Could not get the lock within 30 seconds, so gave up'));
      } else {
        resolve();
      }
    });
  });
}

const configured = axios.defaults.transformRequest ?? [];
const defaultTransformRequest = Array.isArray(configured)
  ? configured
  : [configured];

export const axiosIconikApiClient = axios.create({
  timeout: 30000,
  paramsSerializer: {
    serialize: (paramsObj) =>
      qs.stringify(paramsObj, { arrayFormat: 'repeat' }),
  },
  // Replaces the default response parse so the reviver runs (it still
  // JSON.parses) and restores the int64-min/max sort sentinels losslessly.
  transformResponse: [parseI64SentinelResponse],
  // Default stringify (plus content-type and FormData/Blob handling) runs
  // first, then we unquote the sentinel markers in the final JSON string.
  transformRequest: [...defaultTransformRequest, encodeI64SentinelRequest],
});

throttle(axiosIconikApiClient, {
  throttleThreshold: 10,

  maxTokens: 1000,
  refillTime: 1,
  refillAmount: 50,
});

const multipartUrlRegEx =
  /^\/API\/files\/v1\/[a-z]{5,10}\/[a-z0-9-]{36}\/files\/[a-z0-9-]{36}\/multipart/g;

axiosRetry(axiosIconikApiClient, {
  retryDelay: exponentialDelay,
  retryCondition: (error) =>
    isSafeRequestError(error) ||
    error.response?.status === 429 ||
    Boolean(
      (error.config?.method === 'post' &&
        error.response?.status === 500 &&
        error.config?.url === '/API/search/v1/search/') ||
        error.config?.url?.match(multipartUrlRegEx),
    ),
});

const REQUESTS_LIMIT = 100;

// Browser session requests keyed by request ID, used to distinguish websocket notifications
// between the ones initiated in the current browser tab (where we might handle things manually) and
// other local or remote sessions. Each entry holds the request's path params, so a
// notification can also be matched against the entity that request actually wrote.
export const sessionRequests = new Map<string, EndpointRequest['params']>();

function addRequest(
  requestId: string,
  params: EndpointRequest['params'],
): void {
  sessionRequests.set(requestId, params);

  // + 50 elements is a buffer to avoid cleaning the requests storage
  // on every request, so when it comes to REQUESTS_LIMIT + 50 we're removing
  // the oldest elements until only REQUESTS_LIMIT elements will be left in the storage
  if (sessionRequests.size > REQUESTS_LIMIT + 50) {
    const toRemove = sessionRequests.size - REQUESTS_LIMIT;
    const ids = [...sessionRequests.keys()];
    for (const id of ids.slice(0, toRemove)) {
      sessionRequests.delete(id);
    }
  }
}

export const STORAGE_APP_ID = 'appId';
export const STORAGE_TOKEN = 'token';
export const STORAGE_TOKEN_LAST_CHECKED_AT = 'tokenLastCheckedAt';
export const STORAGE_TOKEN_MODIFIED_AT = 'tokenModifiedAt';

export interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
}

type Options<State extends BaseAppState = BaseAppState> = {
  // Storage interface to use for storing the token and other data.
  // Must synchronous to avoid race conditions during the
  // token refresh stages across various instances of
  // the middleware that utilize the same token.
  storage: StorageInterface;

  getRequestOptions: (
    api: AppMiddlewareAPI<State>,
    endpointRequest: EndpointRequestWeb,
  ) => EndpointRequestWeb;

  canRefreshToken: (
    api: AppMiddlewareAPI<State>,
    endpointRequest: EndpointRequestWeb,
  ) => boolean | undefined;

  onTokenRefreshed: (
    api: AppMiddlewareAPI<State>,
    endpointRequest: EndpointRequestWeb,
    token: AuthTokenSchema,
  ) => void;

  // Triggered on HTTP 401 error
  onHttpErrorUnauthorized: (
    api: AppMiddlewareAPI<State>,
    endpointRequest: EndpointRequestWeb,
    error: AxiosError<ErrorResponseBody>,
  ) => void;

  // Triggered on HTTP 400 or >= 404 errors, except for Incorrect login errors
  onHttpError: (
    api: AppMiddlewareAPI<State>,
    endpointRequest: EndpointRequestWeb,
    error: AxiosError<ErrorResponseBody>,
  ) => void;
};

async function refreshToken<State extends BaseAppState = BaseAppState>(
  middlewareAPI: AppMiddlewareAPI<State>,
  endpointRequest: EndpointRequestWeb,
  options: Options<State>,
): Promise<void> {
  try {
    const host = endpointRequest.host ?? '';
    const response = await axios<AuthTokenSchema>({
      url: `${host}/API/auth/v1/auth/token/`,
      method: 'put',
      paramsSerializer: {
        serialize: (paramsObj) =>
          qs.stringify(paramsObj, { arrayFormat: 'repeat' }),
      },
      headers: endpointRequest.headers,
    });
    const tokenData = response.data;

    const currentModifiedAt = options.storage.getItem(
      STORAGE_TOKEN_MODIFIED_AT,
    );
    const modifiedAt = tokenData.date_modified;

    if (
      !currentModifiedAt ||
      (currentModifiedAt &&
        modifiedAt &&
        parseISO(modifiedAt) > parseISO(currentModifiedAt))
    ) {
      options.storage.setItem(STORAGE_APP_ID, tokenData.app_id ?? '');
      options.storage.setItem(STORAGE_TOKEN, tokenData.token);
      options.storage.setItem(STORAGE_TOKEN_MODIFIED_AT, modifiedAt ?? '');
      options.onTokenRefreshed(middlewareAPI, endpointRequest, tokenData);
    }
  } catch (_error) {
    /* empty */
  }
}

type ErrorResponseBody = {
  errors: string[];
};

function addRequestIdToRequest<Request extends EndpointRequestWeb>(
  request: Request,
): Request {
  const requestId = getRandomHex();
  const headers = { ...request.headers };
  if (requestId) {
    addRequest(requestId, request.params);
    headers['X-Request-ID'] = requestId;
  }

  const endpointRequest: Request = {
    ...request,
    headers,
  };

  return endpointRequest;
}

function performAjax<
  Request extends EndpointRequestWeb,
  State extends BaseAppState = BaseAppState,
>(
  middlewareAPI: AppMiddlewareAPI<State>,
  request: Request,
  options: Options<State>,
): Promise<EndpointResponse<Request>> {
  const endpointRequest = addRequestIdToRequest(request);

  const host = endpointRequest.host ?? '';
  return axiosIconikApiClient({
    url: host + endpointRequest.url,
    method: endpointRequest.method,
    data: endpointRequest.body,
    params: endpointRequest.query,
    headers: endpointRequest.headers,
    cancelToken: endpointRequest.meta?.cancel?.token,
    signal: endpointRequest.abortSignal,
  })
    .then(async (response) => {
      // TODO In cases when we require index_immediately backend still
      //  doesn't guarantee that indexing is done so we intentionally wait
      //  for 1000 ms.
      //  https://linear.app/iconik/issue/FE-3016
      //  https://www.elastic.co/docs/deploy-manage/production-guidance/optimize-performance/indexing-speed#_unset_or_increase_the_refresh_interval
      // biome-ignore lint/plugin: the query shape differs per endpoint; this check only probes two optional flags.
      const query = endpointRequest.query as any;

      // Creating asset in a collection is an implicit index_immediately
      // case according to BE sources.
      const creatingAssetInCollection =
        endpointRequest.id === AssetsHttpPostAssets &&
        query?.assign_to_collection;

      // Deleting a portal drops the document synchronously but takes no
      // index_immediately flag, and the portals listing is read back before
      // the index refresh makes that removal visible.
      const deletingPortal =
        endpointRequest.id === AssetsHttpDeletePortalsByPortalId;

      if (
        query?.index_immediately ||
        creatingAssetInCollection ||
        deletingPortal
      ) {
        await sleep(1000);
      }
      return response;
    })
    .then((response) => ({
      body: response.data,
      status: response.status,
      statusText: response.statusText,
      // biome-ignore lint/plugin: axios AxiosHeaders is structurally the HttpHeaders record at this boundary.
      headers: response.headers as HttpHeaders,
      request: endpointRequest,
    }))
    .catch((error: AxiosError<ErrorResponseBody>) => {
      const responseData: Partial<AxiosResponse> = error.response ?? {};
      const responseHeaders = responseData.headers ?? {};
      const responseStatus = responseData.status ?? 0;
      if (responseStatus === 401) {
        if (!endpointRequest.meta?.keepCredentialsOnUnauthorized) {
          options.storage.removeItem(STORAGE_TOKEN_LAST_CHECKED_AT);
          options.storage.removeItem(STORAGE_TOKEN_MODIFIED_AT);
          options.storage.removeItem(STORAGE_TOKEN);
        }

        options.onHttpErrorUnauthorized(middlewareAPI, endpointRequest, error);
      }

      if (responseStatus === 400 || responseStatus > 404) {
        // Prevent sending Error report for Incorrect login errors
        let incorrectLoginError = false;
        if (endpointRequest.url === API_LOGIN_URL && error.response) {
          incorrectLoginError =
            error.response.data?.errors?.[0] ===
            'The username and/or password are incorrect';
        }

        if (!incorrectLoginError) {
          options.onHttpError(middlewareAPI, endpointRequest, error);
        }
      }

      throw new EndpointError(
        endpointRequest,
        {
          request: endpointRequest,
          status: responseStatus,
          // Convert axios headers specific object to plain object
          headers: Object.assign(Object.create(null), responseHeaders),
          body: responseData.data,
        },
        error,
      );
    });
}

function shouldRefreshToken<State extends BaseAppState = BaseAppState>(
  options: Options<State>,
): boolean {
  const tokenLastChecked = options.storage.getItem(
    STORAGE_TOKEN_LAST_CHECKED_AT,
  );

  if (!tokenLastChecked) {
    options.storage.setItem(STORAGE_TOKEN_LAST_CHECKED_AT, String(Date.now()));
  }
  const secondsSinceLastRefresh = tokenLastChecked
    ? (Date.now() - parseInt(tokenLastChecked, 10)) / 1000
    : 0;
  return secondsSinceLastRefresh > 3600;
}

function getRequestOptions<State extends BaseAppState = BaseAppState>(
  api: AppMiddlewareAPI<State>,
  endpointRequest: EndpointRequestWeb,
  options: Options<State>,
): EndpointRequestWeb {
  const result = options.getRequestOptions(api, endpointRequest);
  const appId = options.storage.getItem(STORAGE_APP_ID);
  const token = options.storage.getItem(STORAGE_TOKEN);

  const requestHeaders: Record<string, string> = {};
  const headers = result.headers ?? {};
  if (appId && token) {
    if (!headers['App-ID']) {
      requestHeaders['App-ID'] = appId;
    }
    if (!headers['Auth-Token'] && !headers['Share-Auth-Token']) {
      requestHeaders['Auth-Token'] = token;
    }
  }

  return {
    ...result,
    headers: {
      ...headers,
      ...requestHeaders,
    },
  };
}

export function createHttpMiddleware<State extends BaseAppState = BaseAppState>(
  options: Options<State>,
): AppMiddleware<State> {
  return (middlewareAPI) => (next) => async (action) => {
    if (!isEndpointRequest(action)) {
      return next(action);
    }

    const canRefresh =
      options.canRefreshToken(middlewareAPI, action) ??
      action.meta?.canRefresh ??
      true;

    if (shouldRefreshToken(options) && canRefresh) {
      await lock();

      // There can be more than one request which hits the lock but only
      // one should make a token refresh.
      if (shouldRefreshToken(options)) {
        try {
          const endpointRequest = getRequestOptions(
            middlewareAPI,
            action,
            options,
          );
          await refreshToken(middlewareAPI, endpointRequest, options);
          options.storage.setItem(
            STORAGE_TOKEN_LAST_CHECKED_AT,
            String(Date.now()),
          );
        } finally {
          mutex.unlock();
        }
      } else {
        // Others unlock immediately.
        mutex.unlock();
      }
    }

    // Always get an updated config so the token will be always up-to-date.
    const endpointRequest = getRequestOptions(middlewareAPI, action, options);
    return performAjax(middlewareAPI, endpointRequest, options);
  };
}
